// Chemin: C:\gestion-copro\src\components\messagerie\ReplyEmailModal.tsx
// Modale de reponse email - Copro Protect
// Session 45 : Reponse email depuis l'app avec protection SCUD optionnelle
// Session 49 : Ajout tracabilite (userId, userNom) + icones Lucide
// Session 80 : Ajout coproprieteId pour gestion quota SENTINELLE

import React, { useState } from 'react';
import { X, Send, Shield, ShieldCheck, ShieldAlert, Loader2, AlertTriangle } from 'lucide-react';
import { EmailDetail } from '../../types/gmail.types';
import { sendEmail, prepareReplyParams } from '../../services/gmail.service';
import { analyserCourrierSCUD } from '../../services/courrier.service';
import { AnalyseSCUD, AlerteSCUD } from '../../types/courrier.types';
import { useAuth } from '../../context/AuthContext';

interface ReplyEmailModalProps {
  email: EmailDetail;
  coproprieteId: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

type ModalStep = 'compose' | 'confirm' | 'analyze' | 'sending';

const ReplyEmailModal: React.FC<ReplyEmailModalProps> = ({
  email,
  coproprieteId,
  onClose,
  onSuccess,
}) => {
  const { firebaseUser, userProfile } = useAuth();
  
  // Preparer les parametres de reponse
  const replyParams = prepareReplyParams({
    id: email.id,
    threadId: email.threadId,
    fromEmail: email.fromEmail,
    subject: email.subject,
  });

  // Etats
  const [step, setStep] = useState<ModalStep>('compose');
  const [to] = useState(replyParams.to || '');
  const [subject] = useState(replyParams.subject || '');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Etat analyse SCUD
  const [analyseSCUD, setAnalyseSCUD] = useState<AnalyseSCUD | null>(null);

  // Passer a l'etape de confirmation
  const handleContinue = () => {
    if (!body.trim()) {
      setError('Le message ne peut pas etre vide.');
      return;
    }
    setError(null);
    setStep('confirm');
  };

  // Envoyer directement sans analyse
  const handleSendDirect = async () => {
    await doSendEmail();
  };

  // Analyser puis envoyer
  const handleAnalyzeFirst = async () => {
    setStep('analyze');
    setIsLoading(true);
    setError(null);

    try {
      // Session 80 : Ajout coproprieteId pour gestion quota SENTINELLE
      const result = await analyserCourrierSCUD(
        coproprieteId,
        subject,
        body,
        to,
        'email'
      );
      setAnalyseSCUD(result);
    } catch (err) {
      console.error('Erreur analyse SCUD:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Erreur lors de l\'analyse. Vous pouvez envoyer directement.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Envoi effectif de l'email
  const doSendEmail = async () => {
    setStep('sending');
    setIsLoading(true);
    setError(null);

    try {
      await sendEmail(coproprieteId, {
        to,
        subject,
        body,
        userId: firebaseUser?.uid || '',
        userNom: userProfile?.prenom 
          ? `${userProfile.prenom} ${userProfile.nom}` 
          : userProfile?.nom || firebaseUser?.email || 'Utilisateur',
        threadId: replyParams.threadId,
        inReplyTo: replyParams.inReplyTo,
        references: replyParams.references,
      });

      onSuccess('Email envoye avec succes');
      onClose();
    } catch (err) {
      console.error('Erreur envoi email:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Erreur lors de l\'envoi de l\'email.'
      );
      setStep('confirm');
    } finally {
      setIsLoading(false);
    }
  };

  // Retour a la composition
  const handleBackToCompose = () => {
    setStep('compose');
    setAnalyseSCUD(null);
    setError(null);
  };

  // Retour a la confirmation depuis l'analyse
  const handleBackToConfirm = () => {
    setStep('confirm');
    setError(null);
  };

  // Rendu selon l'etape
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        
        {/* ================================================================
            ETAPE 1 : COMPOSITION
        ================================================================ */}
        {step === 'compose' && (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Repondre a cet email
              </h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Corps */}
            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              {/* Destinataire (lecture seule) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  A
                </label>
                <input
                  type="text"
                  value={to}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                />
              </div>

              {/* Sujet (lecture seule) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sujet
                </label>
                <input
                  type="text"
                  value={subject}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                />
              </div>

              {/* Corps du message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  placeholder="Redigez votre reponse..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>

              {/* Erreur */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleContinue}
                disabled={!body.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continuer
              </button>
            </div>
          </>
        )}

        {/* ================================================================
            ETAPE 2 : CONFIRMATION
        ================================================================ */}
        {step === 'confirm' && (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Confirmer l'envoi
              </h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Corps */}
            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              {/* Resume */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-sm">
                  <span className="font-medium text-gray-700">A :</span>{' '}
                  <span className="text-gray-600">{to}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Sujet :</span>{' '}
                  <span className="text-gray-600">{subject}</span>
                </p>
              </div>

              {/* Apercu du message */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Apercu du message</h3>
                <div className="bg-white border border-gray-200 rounded-lg p-4 text-gray-700 whitespace-pre-wrap text-sm max-h-48 overflow-y-auto">
                  {body}
                </div>
              </div>

              {/* Erreur */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <button
                type="button"
                onClick={handleBackToCompose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors font-medium"
              >
                Modifier
              </button>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleSendDirect}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Envoyer directement
                </button>
                <button
                  type="button"
                  onClick={handleAnalyzeFirst}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  Analyser d'abord
                </button>
              </div>
            </div>
          </>
        )}

        {/* ================================================================
            ETAPE 3 : RESULTAT ANALYSE SCUD
        ================================================================ */}
        {step === 'analyze' && (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  isLoading ? 'bg-blue-100' :
                  analyseSCUD?.scoreRisque === 'faible' ? 'bg-green-100' :
                  analyseSCUD?.scoreRisque === 'moyen' ? 'bg-amber-100' : 'bg-red-100'
                }`}>
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  ) : (
                    analyseSCUD?.scoreRisque === 'faible' ? (
                      <ShieldCheck className="w-5 h-5 text-green-600" />
                    ) : analyseSCUD?.scoreRisque === 'moyen' ? (
                      <Shield className="w-5 h-5 text-amber-600" />
                    ) : (
                      <ShieldAlert className="w-5 h-5 text-red-600" />
                    )
                  )}
                </div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {isLoading ? 'Analyse en cours...' : 'Analyse SENTINELLE'}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Corps */}
            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-4" />
                  <p className="text-gray-600">SENTINELLE analyse votre message...</p>
                </div>
              ) : analyseSCUD ? (
                <>
                  {/* Score de risque */}
                  <div className={`p-4 rounded-lg ${
                    analyseSCUD.scoreRisque === 'faible' ? 'bg-green-50 border border-green-200' :
                    analyseSCUD.scoreRisque === 'moyen' ? 'bg-amber-50 border border-amber-200' :
                    'bg-red-50 border border-red-200'
                  }`}>
                    <p className={`font-medium ${
                      analyseSCUD.scoreRisque === 'faible' ? 'text-green-800' :
                      analyseSCUD.scoreRisque === 'moyen' ? 'text-amber-800' : 'text-red-800'
                    }`}>
                      Niveau de risque : {
                        analyseSCUD.scoreRisque === 'faible' ? 'Faible' :
                        analyseSCUD.scoreRisque === 'moyen' ? 'Moyen' : 'Eleve'
                      }
                    </p>
                  </div>

                  {/* Alertes detectees */}
                  {analyseSCUD.alertes && analyseSCUD.alertes.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="font-medium text-gray-900">
                        Formulations a risque detectees ({analyseSCUD.alertes.length})
                      </h3>
                      {analyseSCUD.alertes.map((alerte: AlerteSCUD, index: number) => (
                        <div key={index} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-amber-800 font-medium">
                            {alerte.message}
                          </p>
                          {alerte.extrait && (
                            <p className="mt-1 text-sm text-amber-700 italic">
                              "{alerte.extrait}"
                            </p>
                          )}
                          {alerte.suggestion && (
                            <p className="mt-2 text-sm text-amber-700">
                              <span className="font-medium">Suggestion :</span> {alerte.suggestion}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Recommandations */}
                  {analyseSCUD.recommandations && analyseSCUD.recommandations.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="font-medium text-gray-900">Recommandations</h3>
                      <ul className="list-disc list-inside text-gray-700 space-y-1">
                        {analyseSCUD.recommandations.map((reco: string, index: number) => (
                          <li key={index}>{reco}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Message si tout est OK */}
                  {analyseSCUD.scoreRisque === 'faible' && (!analyseSCUD.alertes || analyseSCUD.alertes.length === 0) && (
                    <p className="text-green-700">
                      Aucune formulation a risque detectee. Vous pouvez envoyer ce message en confiance.
                    </p>
                  )}
                </>
              ) : error ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              ) : null}
            </div>

            {/* Footer */}
            {!isLoading && (
              <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
                <button
                  type="button"
                  onClick={handleBackToCompose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors font-medium"
                >
                  Modifier le message
                </button>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleBackToConfirm}
                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Retour
                  </button>
                  <button
                    type="button"
                    onClick={doSendEmail}
                    className={`px-4 py-2 rounded-lg text-white font-medium transition-colors flex items-center gap-2 ${
                      analyseSCUD?.scoreRisque === 'eleve' 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    <Send className="w-4 h-4" />
                    {analyseSCUD?.scoreRisque === 'eleve' ? 'Envoyer malgre les risques' : 'Envoyer'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ================================================================
            ETAPE 4 : ENVOI EN COURS
        ================================================================ */}
        {step === 'sending' && (
          <div className="p-8 flex flex-col items-center justify-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
            <p className="text-gray-700">Envoi en cours...</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default ReplyEmailModal;